import React from 'react';
import { Link } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import datacenter from '../assets/industries/data-centers.jpeg';
import infrastructure from '../assets/industries/infrastructure-&-construction.jpeg';
import midstream from '../assets/industries/midstream-&-process-industries.jpeg';
import oilfield from '../assets/industries/oilfield-industry.jpeg';

const IndustryDetail = () => {
  const { industry } = useParams();

  const industryData = {
    'oilfield': {
      title: 'Oilfield Industry',
      description: 'Specialized manufacturing solutions for upstream oil and gas operations.',
      overview: 'With decades of experience serving the oil and gas sector, we understand the demanding requirements of oilfield operations. Our products and services are designed to withstand harsh environments while maintaining the highest safety and reliability standards.',
      services: [
        'BOP Control Systems',
        'Frac Accumulator Systems',
        'Custom Skid Fabrication',
        'Pressure Piping Systems',
        'Wellhead Equipment',
        'Hydrostatic Testing',
      ],
      capabilities: [
        'API certified fabrication',
        'Pressure vessel manufacturing',
        'Field service and support',
        'Emergency repair services',
        'Custom engineering solutions',
        'Compliance with industry standards',
      ],
      imagelink: oilfield,
    },
    'midstream': {
      title: 'Midstream & Process Industries',
      description: 'Precision manufacturing for processing, transportation, and storage operations.',
      overview: 'We provide comprehensive manufacturing solutions for midstream operations including processing facilities, pipeline systems, and storage infrastructure. Our expertise ensures reliable equipment that meets stringent industry requirements.',
      services: [
        'Process Equipment Fabrication',
        'Pipe Spool Manufacturing',
        'Manifold Systems',
        'Separation Equipment',
        'Metering Stations',
        'Custom Skid Packages',
      ],
      capabilities: [
        'ASME code fabrication',
        'Pressure vessel certification',
        'Material traceability',
        'Non-destructive testing',
        'Field installation support',
        'Project management',
      ],
      imagelink: midstream,
    },
    'data-centers': {
      title: 'Data Centers',
      description: 'Precision enclosures and support structures for critical data infrastructure.',
      overview: ' require precision-engineered solutions for housing sensitive equipment. We manufacture custom enclosures, cable management systems, and structural supports designed for the unique demands of data center environments.',
      services: [
        'Server Rack Fabrication',
        'Custom Enclosures',
        'Cable Management Systems',
        'Structural Support Frames',
        'HVAC Housing',
        'Power Distribution Cabinets',
      ],
      capabilities: [
        'Precision sheet metal fabrication',
        'Powder coating and finishing',
        'Tight tolerance manufacturing',
        'Clean room assembly',
        'Rapid prototyping',
        'Volume production',
      ],
      imagelink: datacenter,
    },
    'infrastructure': {
      title: 'Infrastructure & Construction',
      description: 'Heavy fabrication and structural solutions for construction projects.',
      overview: 'Supporting infrastructure and construction projects with heavy structural fabrication, architectural metalwork, and custom solutions. Our capabilities range from structural steel to architectural features.',
      services: [
        'Structural Steel Fabrication',
        'Architectural Metalwork',
        'Bridge Components',
        'Support Structures',
        'Custom Fabrication',
        'Pre-fabricated Assemblies',
      ],
      capabilities: [
        'Certified welding',
        'Large-scale fabrication',
        'Structural engineering support',
        'Site coordination',
        'Quality documentation',
        'Project scheduling',
      ],
      imagelink: infrastructure,
    },
  };

  const currentIndustry = industryData[industry] || industryData['oilfield'];

  return (
    <div className="min-h-screen pt-20" data-testid="industry-detail-page">
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 overflow-hidden h-[30rem]" data-testid="industry-detail-hero">
        <div className="absolute inset-0">
          <img
            src={currentIndustry.imagelink}
            alt={currentIndustry.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/30 to-transparent"></div>
        </div>
        <div className="max-w-7xl mx-auto relative z-10 pt-24">
          <h1 className="text-4xl sm:text-5xl font-bold text-white heading-font mb-6" data-testid="industry-detail-heading">
            {currentIndustry.title}
          </h1>
          <p className="text-xl text-blue-100 max-w-3xl">
            {currentIndustry.description}
          </p>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white" data-testid="industry-detail-content">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 heading-font mb-6">Industry Overview</h2>
            <p className="text-gray-600 leading-relaxed text-lg">
              {currentIndustry.overview}
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 heading-font mb-6">Services for This Industry</h3>
              <ul className="space-y-3">
                {currentIndustry.services.map((service, index) => (
                  <li key={index} className="flex items-start space-x-3" data-testid={`service-${index}`}>
                    <CheckCircle2 className="w-6 h-6 text-blue-700 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{service}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-gray-900 heading-font mb-6">Our Capabilities</h3>
              <ul className="space-y-3">
                {currentIndustry.capabilities.map((capability, index) => (
                  <li key={index} className="flex items-start space-x-3" data-testid={`capability-${index}`}>
                    <CheckCircle2 className="w-6 h-6 text-blue-700 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{capability}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50" data-testid="industry-cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-900 heading-font mb-6">Let's Discuss Your Project</h2>
          <p className="text-gray-600 mb-8">
            Ready to learn more about how we can support your specific needs in this industry?
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 bg-blue-700 text-white rounded-lg hover:bg-blue-800 font-semibold transition-all"
              data-testid="industry-contact-button"
            >
              Contact Us
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <Link
              to="/industries"
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-blue-700 rounded-lg hover:bg-gray-50 font-semibold transition-all border-2 border-blue-700"
              data-testid="industry-back-button"
            >
              View All Industries
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default IndustryDetail;